import React from 'react';
import Icon from '../../../components/AppIcon';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Button from '../../../components/ui/Button';


const AnalyticsPanel = () => {
  const reportGenerationData = [
    { month: 'Jan', reports: 12, views: 145 },
    { month: 'Feb', reports: 15, views: 189 },
    { month: 'Mar', reports: 18, views: 234 },
    { month: 'Apr', reports: 22, views: 298 },
    { month: 'May', reports: 19, views: 267 },
    { month: 'Jun', reports: 25, views: 356 }
  ];

  const popularReports = [
    { name: 'Comprehensive Financial Plan', downloads: 89, trend: '+12%' },
    { name: 'Asset Allocation Analysis', downloads: 76, trend: '+8%' },
    { name: 'Retirement Readiness', downloads: 65, trend: '+15%' },
    { name: 'Cash Flow Projections', downloads: 54, trend: '+5%' },
    { name: 'Scenario Analysis', downloads: 43, trend: '+22%' }
  ];

  const keyMetrics = [
    {
      label: 'Total Reports Generated',
      value: '234',
      change: '+18%',
      trend: 'up',
      icon: 'FileText'
    },
    {
      label: 'Average Generation Time',
      value: '2.3s',
      change: '-12%',
      trend: 'down',
      icon: 'Clock'
    },
    {
      label: 'Export Success Rate',
      value: '98.7%',
      change: '+0.3%',
      trend: 'up',
      icon: 'CheckCircle'
    },
    {
      label: 'User Satisfaction',
      value: '4.8/5',
      change: '+0.2',
      trend: 'up',
      icon: 'Star'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {keyMetrics.map((metric, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <Icon name={metric.icon} size={20} className="text-primary" />
              <div className={`
                text-xs font-medium px-2 py-1 rounded-full
                ${metric.trend === 'up' ?'text-success bg-success/10' :'text-accent bg-accent/10'
                }
              `}>
                {metric.change}
              </div>
            </div>
            <div className="text-2xl font-bold text-foreground mb-1">{metric.value}</div>
            <div className="text-sm text-muted-foreground">{metric.label}</div>
          </div>
        ))}
      </div>

      {/* Report Generation Trends */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Report Generation Trends</h3>
            <p className="text-sm text-muted-foreground">Monthly report generation and view statistics</p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span className="text-muted-foreground">Reports Generated</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-accent rounded-full"></div>
              <span className="text-muted-foreground">Total Views</span>
            </div>
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={reportGenerationData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="views" 
                stackId="1"
                stroke="var(--color-accent)" 
                fill="var(--color-accent)"
                fillOpacity={0.3}
              />
              <Area 
                type="monotone" 
                dataKey="reports" 
                stackId="2"
                stroke="var(--color-primary)" 
                fill="var(--color-primary)"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Popular Reports */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Most Popular Reports</h3>
            <p className="text-sm text-muted-foreground">Top downloaded reports this month</p>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="BarChart3" size={16} className="mr-2" />
            View All
          </Button>
        </div>
        <div className="space-y-4">
          {popularReports.map((report, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-sm font-semibold text-primary">#{index + 1}</span>
                </div>
                <div>
                  <div className="font-medium text-foreground">{report.name}</div>
                  <div className="text-sm text-muted-foreground">{report.downloads} downloads</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-success">{report.trend}</div>
                <Icon name="TrendingUp" size={16} className="text-success" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
            <p className="text-sm text-muted-foreground">Latest report generation and export activities</p>
          </div>
        </div>
        <div className="space-y-3">
          {[
            { action: 'Generated Comprehensive Financial Plan', time: '2 minutes ago', status: 'success' },
            { action: 'Exported Asset Allocation Analysis to PDF', time: '15 minutes ago', status: 'success' },
            { action: 'Created Custom Report Template', time: '1 hour ago', status: 'info' },
            { action: 'Generated Scenario Analysis Report', time: '2 hours ago', status: 'success' },
            { action: 'Export failed - Retry needed', time: '3 hours ago', status: 'error' }
          ].map((activity, index) => (
            <div key={index} className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg transition-colors duration-200">
              <div className={`
                w-2 h-2 rounded-full
                ${activity.status === 'success' ? 'bg-success' : 
                  activity.status === 'error' ? 'bg-destructive' : 'bg-primary'}
              `}></div>
              <div className="flex-1">
                <div className="text-sm text-foreground">{activity.action}</div>
                <div className="text-xs text-muted-foreground">{activity.time}</div>
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;